import * as React from 'react'

import { TFProductCategoryBase } from '~graphql/generated/clientGraphql'
import {
  generatePlaceholderRollingOptionsItems,
  RollingOptions,
} from '~ui/components/RollingOptions/RollingOptions'
import { VERTICAL_ALIGN_OFFSET } from '~ui/components/RollingOptions/RollingOptionsButton'
import { ScrollOverflow } from '~ui/components/ScrollOverflow/ScrollOverflow'

type TRollingOptionsProps = Pick<
  PropsOf<typeof RollingOptions>,
  `onSelection` | `bgColor` | `shadow`
>

type TProps = NoChildren &
  TRollingOptionsProps & {
    /**
     * direction of the buttons, defaults to `column`
     */
    direction?: PropsOf<typeof RollingOptions>['direction']
    /**
     * id of the selected category
     */
    selectedId: Nullable<PropsOf<typeof RollingOptions>['selectedId']>
    /**
     * if the rolling options should be wrapped in scroll overflow,
     * pass the unique id in case there would be more scroll overflow on the page
     *
     * *Note: In that case the direction will always be 'row'.*
     */
    scrollOverflowId?: string
    /**
     * how much (px) the content should scroll when you click the scroll button
     */
    buttonShiftValue?: number
    /**
     * data to render, it can be any array of categories that have an `id` and a `nameLabel` properties
     */
    categories: Nullable<RoA<TFProductCategoryBase>>
  }

/**
 * Rolling options of product or addon categories,
 * displays category nameLabels
 */
export const CategoryNavigation: React.FC<TProps> = ({
  scrollOverflowId,
  selectedId,
  onSelection,
  direction = 'column',
  categories,
  buttonShiftValue,
  bgColor,
  shadow = `none`,
}) => {
  // use real product categories OR generate placeholder ones for loading skeleton if there is no data
  const categoryButtons = categories?.map(({ id, nameLabel }) => ({
    id,
    label: nameLabel,
  }))

  const categoryOrPlaceholderButtons = categoryButtons ?? generatePlaceholderRollingOptionsItems(10)
  const selectedButton = categoryOrPlaceholderButtons.find((item) => item.id === selectedId)

  const availableSelectedId = selectedButton
    ? selectedButton.id
    : categoryOrPlaceholderButtons[0]?.id

  const renderButtons = (containerId?: PropsOf<typeof RollingOptions>['optionsContainerId']) => (
    <RollingOptions
      bgColor={bgColor}
      shadow={shadow}
      maxWidth={`15rem`}
      optionsContainerId={containerId}
      direction={scrollOverflowId ? `row` : direction}
      showSkeleton={!categories}
      items={categoryOrPlaceholderButtons}
      selectedId={availableSelectedId}
      onSelection={onSelection}
      variant={`solid`}
      padding={{ vertical: `smallMedium`, horizontal: `medium` }}
    />
  )

  if (scrollOverflowId) {
    return (
      <ScrollOverflow
        recalculateIndicator={categories}
        id={`CategoryButtons${scrollOverflowId}`}
        selectedElementIndex={categories?.findIndex((category) => category.id === selectedId)}
        buttonShiftValue={buttonShiftValue}
        buttonPosition={{
          position: `center`,
          verticalOffset: VERTICAL_ALIGN_OFFSET,
        }}
        withNavContainer
      >
        {({ itemContainerElementId }) => renderButtons(itemContainerElementId)}
      </ScrollOverflow>
    )
  }

  return renderButtons()
}
